"use client";
import { createContext, useContext, useEffect, useState } from "react";
import { refreshPlaygroundDraft } from "@/lib/docs/nexum/playground";
import { MAX_REQUEST_BYTES } from "@/lib/docs/nexum/request";
const STORAGE_KEY = "portal-playground-session-v2";
type Draft = {
  token: string;
  parameters: Record<string, string>;
  bodies: Record<string, string>;
};
const emptyDraft = (): Draft => ({ token: "", parameters: {}, bodies: {} });
const Context = createContext<{
  parameters: Record<string, string>;
  setParameter: (name: string, value: string) => void;
  bodyFor: (key: string, example: string) => string;
  setBodyFor: (key: string, value: string) => void;
  clearDraft: () => void;
  token: string;
  setToken: (token: string) => void;
} | null>(null);
function strings(value: unknown, limit: number): Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter(
      ([key, item]) =>
        !["__proto__", "constructor", "prototype"].includes(key) &&
        typeof item === "string" &&
        item.length <= limit,
    ),
  );
}
export function ApiSessionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    try {
      // Retire the editable-server contract rather than trusting saved destinations.
      sessionStorage.removeItem("portal-api-session-v1");
      const raw = sessionStorage.getItem(STORAGE_KEY);
      const saved = raw && raw.length <= 1024 * 1024 ? JSON.parse(raw) : null;
      if (saved && typeof saved === "object")
        setDraft({
          token:
            typeof saved.token === "string" && saved.token.length <= 16384
              ? saved.token
              : "",
          parameters: strings(saved.parameters, 256),
          bodies: strings(saved.bodies, MAX_REQUEST_BYTES),
        });
    } catch {
      /* Storage is optional; in-memory session still works. */
    }
    setLoaded(true);
  }, []);
  useEffect(() => {
    if (!loaded) return;
    try {
      const raw = JSON.stringify(draft);
      if (raw.length <= 1024 * 1024) sessionStorage.setItem(STORAGE_KEY, raw);
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* Storage unavailable. */
    }
  }, [loaded, draft]);
  return (
    <Context.Provider
      value={{
        token: draft.token,
        setToken: (token) => setDraft((current) => ({ ...current, token })),
        parameters: draft.parameters,
        setParameter: (name, value) =>
          setDraft((current) => ({
            ...current,
            parameters: { ...current.parameters, [name]: value },
          })),
        bodyFor: (key, example) =>
          refreshPlaygroundDraft(
            Object.hasOwn(draft.bodies, key) ? draft.bodies[key] : example,
            key,
            example,
          ),
        setBodyFor: (key, value) =>
          setDraft((current) => ({
            ...current,
            bodies: { ...current.bodies, [key]: value },
          })),
        clearDraft: () => setDraft(emptyDraft()),
      }}
    >
      {children}
    </Context.Provider>
  );
}
export function useApiSession() {
  const value = useContext(Context);
  if (!value) throw new Error("Missing API session provider");
  return value;
}
